import React from 'react';
import styled, { css } from 'styled-components';
import * as CSS from 'csstype';
import { borders, colors, spaces, typography } from '../../settings';
import { resolveResponsiveProp, ResponsiveProp } from '../../lib/responsiveProps';
import { Spinner } from '../Spinner';
import {
    Clickable,
    ClickableProps,
    ClickableComponent,
    ClickableWithRoutingProps,
    ClickableNoRoutingProps,
} from '../Clickable';
import { ButtonAppearance, ButtonColorConfig, ButtonSize, ButtonWidth } from './types';
import {
    createButtonPrimaryColorConfig,
    createButtonSecondaryColorConfig,
    createButtonTertiaryColorConfig,
} from './createButtonColorConfig';
import { Box } from '../Box';

interface IconProp {
    component: React.ComponentType;
    position: 'left' | 'right';
}

interface ButtonStylesProps {
    appearance?: ButtonAppearance | ButtonColorConfig;
    size?: ButtonSize;
    width?: ResponsiveProp<ButtonWidth>;
    selected?: boolean;
    loading?: boolean;
    icon?: React.ComponentType | IconProp;
}

export type ButtonWithRoutingProps = ClickableWithRoutingProps<ButtonStylesProps>;
export type ButtonNoRoutingProps = ClickableNoRoutingProps<ButtonStylesProps>;
export type ButtonProps = ClickableProps<ButtonStylesProps>;
export type ButtonComponent<E = unknown> = ClickableComponent<ButtonStylesProps & E>;

const UnstyledButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
    (props, ref) => {
        const { children, loading = false, disabled, size = 'medium', icon, ...rest } = props;
        const finalDisabled = loading || disabled;
        const spaceKey: keyof typeof elementSpaceMap = icon && !children ? 'iconOnly' : size;
        const space = elementSpaceMap[spaceKey];

        const iconProp = normalizeIconProp(icon);
        const leftAdornment = getLeftAdornment({ space, loading, iconProp });
        const rightAdornment = getRightAdornment({ space, loading, iconProp });

        return (
            <Clickable {...rest} disabled={finalDisabled} ref={ref}>
                <ButtonInner>
                    {leftAdornment}
                    {children}
                    {rightAdornment}
                </ButtonInner>
            </Clickable>
        );
    }
);

export const Button = styled(UnstyledButton)(buttonStyles) as ButtonComponent;

// ButtonInner is required to fix a cross-browser issue with some browsers ( e.g. older desktop/mobile Safari )
// not being able to apply display: inline-grid on a button.
// We must also keep these rules in `buttonStyles` as well for it to work the same for new browsers.
// https://stackoverflow.com/questions/35464067/flex-grid-layouts-not-working-on-button-or-fieldset-elements
const ButtonInner = styled.span`
    display: inline-grid;
    align-items: center;
    justify-content: center;
    grid-auto-flow: column;
    vertical-align: middle;
`;

const widths = {
    auto: 'auto',
    fill: '100%',
};

const elementSpaceMap: Record<ButtonSize | 'iconOnly', spaces.Space> = {
    iconOnly: 'none',
    small: 'space5',
    medium: 'space6',
    large: 'space7',
};

function getColorConfig(
    appearance: ButtonStylesProps['appearance'] = 'primary'
): ButtonColorConfig {
    if (typeof appearance === 'string') {
        return buttonColorMap[appearance];
    }
    return appearance;
}

function buttonStyles(props: ButtonProps) {
    const { appearance, size = 'medium', width = 'auto', selected = false, icon, children } = props;

    let borderRadius: CSS.Property.BorderRadius;
    let borderWidth: CSS.Property.BorderWidth;
    let fontSize: CSS.Property.FontSize;
    let paddingVertical: CSS.Property.Padding;
    let paddingHorizontal: CSS.Property.Padding;
    let minHeight: CSS.Property.MinHeight;
    let minWidth: CSS.Property.MinWidth;
    let fontFamily: CSS.Property.FontFamily = typography.fontFamilies.text;

    switch (size) {
        case 'small':
            borderRadius = `${borders.borderRadiuses.small}px`;
            fontSize = `${typography.typeScale.size2}px`;
            borderWidth = '2px';
            paddingVertical = `2px`;
            paddingHorizontal = `${spaces.space6}`;
            minHeight = '24px';
            minWidth = '24px';
            break;
        case 'medium':
            borderRadius = `${borders.borderRadiuses.medium}px`;
            fontSize = `${typography.typeScale.size3}px`;
            borderWidth = '2px';
            paddingVertical = `9px`;
            paddingHorizontal = `${spaces.space8}`;
            minHeight = '44px';
            minWidth = '44px';
            break;
        case 'large':
            borderRadius = `${borders.borderRadiuses.large}px`;
            fontSize = `${typography.typeScale.size4}px`;
            borderWidth = '3px';
            paddingVertical = `12px`;
            paddingHorizontal = `${spaces.gutter}`;
            minHeight = '56px';
            minWidth = '56px';
            fontFamily = `${typography.fontFamilies.heading}`;
            break;
    }

    if (icon && !children) {
        paddingHorizontal = 0;
    }

    const colorConfig = getColorConfig(appearance);
    const disabledButtonColorCombo = colorConfig['disabled'];
    const buttonColorCombo = colorConfig[selected ? 'selected' : 'normal'];

    const normalizedBackgroundColor = colors.getColor(buttonColorCombo.backgroundColor);
    const hoverBackgroundColor = colors.getColor(buttonColorCombo.backgroundColorHover);
    const activeBackgroundColor = colors.getColor(buttonColorCombo.backgroundColorActive);

    const normalizedBorderColor = colors.getColor(buttonColorCombo.borderColor);
    const hoverBorderColor = colors.getColor(buttonColorCombo.borderColorHover);
    const activeBorderColor = colors.getColor(buttonColorCombo.borderColorActive);

    const normalizedTextColor = colors.getColor(buttonColorCombo.textColor);
    const hoverTextColor = colors.getColor(buttonColorCombo.textColorHover);
    const activeTextColor = colors.getColor(buttonColorCombo.textColorActive);

    return css`
        display: inline-grid;
        align-items: center;
        justify-content: center;
        grid-auto-flow: column;
        vertical-align: middle;
        margin: 0;
        padding: ${paddingVertical} ${paddingHorizontal};
        line-height: ${typography.lineHeights.typeScale.size3};
        min-height: ${minHeight};
        min-width: ${minWidth};
        text-align: center;
        font-family: ${fontFamily};
        font-size: ${fontSize};

        background: ${normalizedBackgroundColor};
        border-color: ${normalizedBorderColor};
        color: ${normalizedTextColor};
        border-width: ${borderWidth};
        border-style: solid;

        -moz-appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
        text-decoration: none;

        border-top-right-radius: ${borderRadius};
        border-bottom-right-radius: ${borderRadius};
        border-top-left-radius: ${borderRadius};
        border-bottom-left-radius: ${borderRadius};

        ${resolveResponsiveProp(width, 'width', widths)};

        &:hover,
        &:focus {
            color: ${hoverTextColor};
            background-color: ${hoverBackgroundColor};
            border-color: ${hoverBorderColor};
            text-decoration: none;
            outline: none;
        }

        &:active {
            background-color: ${activeBackgroundColor};
            border-color: ${activeBorderColor};
            color: ${activeTextColor};
        }

        &:disabled,
        &[disabled],
        &[aria-disabled='true'] {
            cursor: not-allowed;
            background: ${colors.getColor(disabledButtonColorCombo.backgroundColor)};
            border-color: ${colors.getColor(disabledButtonColorCombo.borderColor)};
            color: ${colors.getColor(disabledButtonColorCombo.textColor)};

            &:hover,
            &:focus {
                background: ${colors.getColor(disabledButtonColorCombo.backgroundColorHover)};
                border-color: ${colors.getColor(disabledButtonColorCombo.borderColorHover)};
                color: ${colors.getColor(disabledButtonColorCombo.textColorHover)};
            }

            &:active {
                background: ${colors.getColor(disabledButtonColorCombo.backgroundColorActive)};
                border-color: ${colors.getColor(disabledButtonColorCombo.borderColorActive)};
                color: ${colors.getColor(disabledButtonColorCombo.textColorActive)};
            }
        }
    `;
}

type ButtonColorMap = Record<ButtonAppearance, ButtonColorConfig>;
const buttonColorMap: ButtonColorMap = {
    primary: createButtonPrimaryColorConfig({
        color: 'heritage',
        colorHover: 'heritageLight',
        colorActive: 'heritageDark',
    }),
    secondary: createButtonSecondaryColorConfig(),
    tertiary: createButtonTertiaryColorConfig({
        color: 'heritage',
        colorHover: 'heritageLight',
        colorActive: 'heritageDark',
    }),
};

function normalizeIconProp(icon?: React.ComponentType | IconProp): IconProp | undefined {
    if (!icon) {
        return undefined;
    }

    if (typeof icon === 'function') {
        return { component: icon, position: 'left' };
    }

    return icon;
}

interface GetAdornmentParams {
    loading: boolean;
    space: spaces.Space;
    iconProp?: IconProp;
}
function getLeftAdornment({ space, loading, iconProp }: GetAdornmentParams): React.ReactNode {
    if (!loading && iconProp?.position === 'left') {
        return (
            <Box as="span" display="flex" marginRight={space}>
                <iconProp.component />
            </Box>
        );
    }

    if (loading && (!iconProp || iconProp.position === 'left')) {
        return (
            <Box as="span" display="flex" marginRight={space}>
                <Spinner />
            </Box>
        );
    }

    return null;
}
function getRightAdornment({ space, loading, iconProp }: GetAdornmentParams): React.ReactNode {
    if (!loading && iconProp?.position === 'right') {
        return (
            <Box as="span" display="flex" marginLeft={space}>
                <iconProp.component />
            </Box>
        );
    }

    if (loading && iconProp?.position === 'right') {
        return (
            <Box as="span" display="flex" marginLeft={space}>
                <Spinner />
            </Box>
        );
    }

    return null;
}
